import React, { useState } from 'react';
import { CopyIcon, CheckIcon } from 'lucide-react';
const CSS_EXPORT = `/* ============================================
   OPENMART BLOG — WEBFLOW RICH TEXT STYLES
   Apply these to your Rich Text Block element
   Font: Aeonik (or fallback to system sans-serif)
   ============================================ */

/* ---- Base Rich Text Container ---- */
.w-richtext {
  font-family: Aeonik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #475569; /* slate-600 */
  font-size: 17px;
  line-height: 1.85;
  max-width: 680px;
}

/* ---- Headings ---- */
.w-richtext h1 {
  font-size: 3.75rem; /* 60px */
  font-weight: 700;
  color: #0f172a; /* slate-900 */
  line-height: 1.1;
  letter-spacing: -0.025em;
  margin-bottom: 1.5rem;
  margin-top: 0;
}

.w-richtext h2 {
  font-size: 1.875rem; /* 30px */
  font-weight: 700;
  color: #0f172a; /* slate-900 */
  line-height: 1.25;
  margin-bottom: 1.5rem;
  margin-top: 5rem;
}

.w-richtext h3 {
  font-size: 1.25rem; /* 20px */
  font-weight: 700;
  color: #0f172a; /* slate-900 */
  line-height: 1.4;
  margin-bottom: 0.5rem;
  margin-top: 3rem;
}

.w-richtext h4 {
  font-size: 1.125rem; /* 18px */
  font-weight: 700;
  color: #0f172a; /* slate-900 */
  line-height: 1.4;
  margin-bottom: 0.5rem;
  margin-top: 2.5rem;
}

.w-richtext h5 {
  font-size: 1rem; /* 16px */
  font-weight: 700;
  color: #0f172a; /* slate-900 */
  line-height: 1.5;
  margin-bottom: 0.5rem;
  margin-top: 2rem;
}

.w-richtext h6 {
  font-size: 0.875rem; /* 14px */
  font-weight: 700;
  color: #334155; /* slate-700 */
  line-height: 1.5;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  margin-top: 2rem;
}

/* ---- Paragraph ---- */
.w-richtext p {
  font-size: 17px;
  color: #475569; /* slate-600 */
  line-height: 1.85;
  margin-bottom: 2rem;
}

/* ---- Blockquote ---- */
.w-richtext blockquote {
  border-left: 2px solid #2563eb; /* blue-600 */
  padding-left: 2rem;
  margin: 3rem 0;
  font-size: 1.25rem; /* 20px */
  color: #334155; /* slate-700 */
  font-weight: 500;
  line-height: 1.85;
  font-style: normal;
}

/* ---- Ordered List ---- */
.w-richtext ol {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin-bottom: 2rem;
}

.w-richtext ol li {
  font-size: 17px;
  color: #475569; /* slate-600 */
  line-height: 1.85;
  margin-bottom: 0.75rem;
  padding-left: 0.5rem;
}

/* ---- Unordered List ---- */
.w-richtext ul {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 2rem;
}

.w-richtext ul li {
  font-size: 17px;
  color: #475569; /* slate-600 */
  line-height: 1.85;
  margin-bottom: 0.75rem;
  padding-left: 0.5rem;
}

/* ---- Links ---- */
.w-richtext a {
  color: #2563eb; /* blue-600 */
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: #93c5fd; /* blue-300 */
  font-weight: 500;
  transition: color 0.15s, text-decoration-color 0.15s;
}

.w-richtext a:hover {
  color: #1d4ed8; /* blue-700 */
  text-decoration-color: #2563eb; /* blue-600 */
}

/* ---- Bold ---- */
.w-richtext strong {
  font-weight: 700;
  color: #0f172a; /* slate-900 */
}

/* ---- Emphasis ---- */
.w-richtext em {
  font-style: italic;
  color: inherit;
}

/* ---- Superscript / Subscript ---- */
.w-richtext sup {
  font-size: 0.75em;
  vertical-align: super;
  line-height: 0;
}

.w-richtext sub {
  font-size: 0.75em;
  vertical-align: sub;
  line-height: 0;
}

/* ---- Figure / Image ---- */
.w-richtext figure {
  margin: 2.5rem 0;
}

.w-richtext figure img {
  width: 100%;
  border-radius: 1rem;
  border: 1px solid #f1f5f9; /* slate-100 */
}

.w-richtext figcaption {
  font-size: 0.875rem;
  color: #94a3b8; /* slate-400 */
  text-align: center;
  margin-top: 0.75rem;
}

/* ---- Horizontal Rule ---- */
.w-richtext hr {
  border: none;
  border-top: 1px solid #e2e8f0; /* slate-200 */
  margin: 3rem 0;
}`;
function CopyButton({
  text,
  label
}: {
  text: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <button onClick={handleCopy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors" data-id="element-6969">
      {copied ? <>
          <CheckIcon className="w-4 h-4" data-id="element-6970" />
          Copied!
        </> : <>
          <CopyIcon className="w-4 h-4" data-id="element-6971" />
          {label}
        </>}
    </button>;
}
function StyleNote({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="text-[13px] font-mono text-slate-400 mb-3 bg-slate-50 rounded-lg px-3 py-1.5 inline-block" data-id="element-6972">
      {children}
    </div>;
}
export function RichTextPreview() {
  const [showCSS, setShowCSS] = useState(false);
  return <div className="min-w-full bg-slate-50/50 flex flex-col min-h-screen" style={{
    fontFamily: 'Aeonik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-6973">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10" data-id="element-6974">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between" data-id="element-6975">
          <div data-id="element-6976">
            <h1 className="text-xl font-bold text-slate-900" data-id="element-6977">
              Openmart Blog — Rich Text Style Guide
            </h1>
            <p className="text-sm text-slate-500 mt-1" data-id="element-6978">
              Reference for Webflow CMS Rich Text Block styling
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-6979">
            <button onClick={() => setShowCSS(!showCSS)} className="px-4 py-2.5 bg-white border border-slate-200 text-sm font-semibold text-slate-700 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-6980">
              {showCSS ? 'Hide CSS' : 'View CSS'}
            </button>
            <CopyButton text={CSS_EXPORT} label="Copy CSS" data-id="element-6981" />
          </div>
        </div>
      </div>

      {/* CSS Panel */}
      {showCSS && <div className="bg-slate-900 border-b border-slate-700" data-id="element-6982">
          <div className="max-w-4xl mx-auto px-6 py-6" data-id="element-6983">
            <div className="flex items-center justify-between mb-4" data-id="element-6984">
              <span className="text-sm font-semibold text-slate-300" data-id="element-6985">
                Webflow Custom CSS — paste into Site Settings → Custom Code →
                Head
              </span>
              <CopyButton text={'<style>{`\n' + CSS_EXPORT + '\n`}</style>'} label="Copy with style tags" data-id="element-6986" />
            </div>
            <pre className="text-[13px] leading-relaxed text-emerald-400 font-mono overflow-x-auto max-h-[400px] overflow-y-auto" data-id="element-6987">
              {CSS_EXPORT}
            </pre>
          </div>
        </div>}

      {/* Preview Area */}
      <div className="max-w-4xl mx-auto px-6 py-16 w-full" data-id="element-6988">
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-10 md:p-16" data-id="element-6989">
          {/* Max-width container matching article */}
          <div className="max-w-[680px]" data-id="element-6990">
            {/* ---- HEADINGS ---- */}
            <div className="mb-16" data-id="element-6991">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-6992">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-6993">
                  Headings
                </span>
              </div>

              <StyleNote data-id="element-6994">
                H1 — text-4xl md:text-5xl lg:text-6xl / font-bold / slate-900 /
                leading-[1.1] / tracking-tight
              </StyleNote>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-10 tracking-tight leading-[1.1]" data-id="element-6995">
                Heading 1
              </h1>

              <StyleNote data-id="element-6996">
                H2 — text-2xl md:text-3xl / font-bold / slate-900 /
                leading-tight
              </StyleNote>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 leading-tight" data-id="element-6997">
                Heading 2
              </h2>

              <StyleNote data-id="element-6998">
                H3 — text-xl / font-bold / slate-900 / leading-snug
              </StyleNote>
              <h3 className="text-xl font-bold text-slate-900 mb-6 leading-snug" data-id="element-6999">
                Heading 3
              </h3>

              <StyleNote data-id="element-7000">H4 — text-lg / font-bold / slate-900</StyleNote>
              <h4 className="text-lg font-bold text-slate-900 mb-4" data-id="element-7001">
                Heading 4
              </h4>

              <StyleNote data-id="element-7002">H5 — text-base / font-bold / slate-900</StyleNote>
              <h5 className="text-base font-bold text-slate-900 mb-4" data-id="element-7003">
                Heading 5
              </h5>

              <StyleNote data-id="element-7004">
                H6 — text-sm / font-bold / slate-700 / uppercase /
                tracking-wider
              </StyleNote>
              <h6 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4" data-id="element-7005">
                Heading 6
              </h6>
            </div>

            {/* ---- BODY TEXT ---- */}
            <div className="mb-16" data-id="element-7006">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7007">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7008">
                  Body Text
                </span>
              </div>

              <StyleNote data-id="element-7009">
                p — text-[17px] md:text-lg / slate-600 / leading-[1.85] / mb-8
              </StyleNote>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-7010">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                enim ad minim veniam, quis nostrud exercitation ullamco laboris
                nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor
                in reprehenderit in voluptate velit esse cillum dolore eu fugiat
                nulla pariatur.
              </p>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-7011">
                Excepteur sint occaecat cupidatat non proident, sunt in culpa
                qui officia deserunt mollit anim id est laborum. Sed ut
                perspiciatis unde omnis iste natus error sit voluptatem
                accusantium doloremque laudantium.
              </p>
            </div>

            {/* ---- BLOCKQUOTE ---- */}
            <div className="mb-16" data-id="element-7012">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7013">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7014">
                  Block Quote
                </span>
              </div>

              <StyleNote data-id="element-7015">
                blockquote — border-l-2 border-blue-600 / pl-6 / text-xl /
                slate-700 / font-medium / leading-[1.85]
              </StyleNote>
              <blockquote className="border-l-2 border-blue-600 pl-6 md:pl-8 my-8" data-id="element-7016">
                <p className="text-xl text-slate-700 font-medium leading-[1.85]" data-id="element-7017">
                  "The best sales teams score territories on these dimensions
                  before assigning a single rep. Data-driven territory planning
                  is the foundation of modern local sales."
                </p>
              </blockquote>
            </div>

            {/* ---- ORDERED LIST ---- */}
            <div className="mb-16" data-id="element-7018">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7019">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7020">
                  Ordered List
                </span>
              </div>

              <StyleNote data-id="element-7021">
                ol — list-decimal / pl-6 / text-[17px] / slate-600 /
                leading-[1.85]
              </StyleNote>
              <ol className="list-decimal pl-6 mb-8 space-y-3" data-id="element-7022">
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7023">
                  Start with business density — count target businesses per zip
                  code
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7024">
                  Layer in growth signals like new registrations and
                  construction permits
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7025">
                  Assess contact availability and verified owner coverage rates
                </li>
              </ol>
            </div>

            {/* ---- UNORDERED LIST ---- */}
            <div className="mb-16" data-id="element-7026">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7027">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7028">
                  Unordered List
                </span>
              </div>

              <StyleNote data-id="element-7029">
                ul — list-disc / pl-6 / text-[17px] / slate-600 / leading-[1.85]
              </StyleNote>
              <ul className="list-disc pl-6 mb-8 space-y-3" data-id="element-7030">
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7031">
                  High density of your target business type
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7032">
                  Growth signals: new business registrations, construction
                  permits
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7033">
                  Accessible decision-makers with verified contact information
                </li>
              </ul>
            </div>

            {/* ---- INLINE STYLES ---- */}
            <div className="mb-16" data-id="element-7034">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7035">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7036">
                  Inline Styles
                </span>
              </div>

              <div className="space-y-6" data-id="element-7037">
                <div data-id="element-7038">
                  <StyleNote data-id="element-7039">
                    a — text-blue-600 / underline / underline-offset-[3px] /
                    decoration-blue-300 / font-medium
                  </StyleNote>
                  <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-7040">
                    <a href="#" className="text-blue-600 underline underline-offset-[3px] decoration-blue-300 font-medium hover:text-blue-700 hover:decoration-blue-600 transition-colors" data-id="element-7041">
                      Text link
                    </a>
                  </p>
                </div>

                <div data-id="element-7042">
                  <StyleNote data-id="element-7043">strong — font-bold / slate-900</StyleNote>
                  <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-7044">
                    <strong className="font-bold text-slate-900" data-id="element-7045">
                      Bold text
                    </strong>
                  </p>
                </div>

                <div data-id="element-7046">
                  <StyleNote data-id="element-7047">em — italic / color inherit</StyleNote>
                  <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-7048">
                    <em data-id="element-7049">Emphasis text</em>
                  </p>
                </div>

                <div data-id="element-7050">
                  <StyleNote data-id="element-7051">sup — text-[0.75em] / align super</StyleNote>
                  <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-7052">
                    Superscript
                    <sup className="text-[0.75em] align-super leading-none" data-id="element-7053">
                      1
                    </sup>
                  </p>
                </div>

                <div data-id="element-7054">
                  <StyleNote data-id="element-7055">sub — text-[0.75em] / align sub</StyleNote>
                  <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-7056">
                    Subscript
                    <sub className="text-[0.75em] align-sub leading-none" data-id="element-7057">
                      2
                    </sub>
                  </p>
                </div>
              </div>
            </div>

            {/* ---- FIGURE / IMAGE ---- */}
            <div className="mb-16" data-id="element-7058">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7059">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7060">
                  Figure / Image
                </span>
              </div>

              <StyleNote data-id="element-7061">
                figure — my-10 / img: w-full rounded-2xl border-slate-100 /
                figcaption: text-sm slate-400 text-center
              </StyleNote>
              <figure className="my-10" data-id="element-7062">
                <img src="https://cdn.magicpatterns.com/uploads/6NmmoxfkR8JKjQw9Ne5VQB/Concepts.png" alt="Example blog image" className="w-full rounded-2xl border border-slate-100" data-id="element-7063" />
                <figcaption className="text-sm text-slate-400 text-center mt-3" data-id="element-7064">
                  Figure caption — describes the image above
                </figcaption>
              </figure>
            </div>

            {/* ---- HORIZONTAL RULE ---- */}
            <div className="mb-16" data-id="element-7065">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7066">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7067">
                  Horizontal Rule
                </span>
              </div>

              <StyleNote data-id="element-7068">hr — border-t border-slate-200 / my-12</StyleNote>
              <hr className="border-t border-slate-200 my-12" data-id="element-7069" />
            </div>

            {/* ---- COMBINED EXAMPLE ---- */}
            <div data-id="element-7070">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100" data-id="element-7071">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full" data-id="element-7072">
                  Combined Example
                </span>
                <span className="text-xs text-slate-400" data-id="element-7073">
                  How a real blog post section looks
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-7074">
                What makes a territory high-value?
              </h2>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-7075">
                High-value territories share a few characteristics that separate
                them from average ones. Understanding these signals is the first
                step toward{' '}
                <strong className="font-bold text-slate-900" data-id="element-7076">
                  data-driven territory planning
                </strong>
                .
              </p>
              <ul className="list-disc pl-6 mb-8 space-y-3" data-id="element-7077">
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7078">
                  High density of your{' '}
                  <a href="#" className="text-blue-600 underline underline-offset-[3px] decoration-blue-300 font-medium hover:text-blue-700 transition-colors" data-id="element-7079">
                    target business type
                  </a>
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7080">
                  Growth signals: new business registrations, construction
                  permits
                </li>
                <li className="text-[17px] md:text-lg text-slate-600 leading-[1.85] pl-2" data-id="element-7081">
                  Accessible decision-makers with <em data-id="element-7082">verified</em> contact
                  information
                </li>
              </ul>
              <blockquote className="border-l-2 border-blue-600 pl-6 md:pl-8 my-8" data-id="element-7083">
                <p className="text-xl text-slate-700 font-medium leading-[1.85]" data-id="element-7084">
                  "The best sales teams score territories on these dimensions
                  before assigning a single rep."
                </p>
              </blockquote>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-7085">
                Once you have these signals, you can build a simple scoring
                model. Learn more in our{' '}
                <a href="#" className="text-blue-600 underline underline-offset-[3px] decoration-blue-300 font-medium hover:text-blue-700 transition-colors" data-id="element-7086">
                  territory planning guide
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        {/* Token Reference Table */}
        <div className="mt-12 bg-white rounded-[2rem] border border-slate-100 shadow-sm p-10 md:p-16 overflow-x-auto" data-id="element-7087">
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-7088">
            Quick Reference
          </h2>
          <p className="text-sm text-slate-500 mb-8" data-id="element-7089">
            Copy these values into Webflow's style panel for each Rich Text
            child element
          </p>

          <table className="w-full text-left text-sm" data-id="element-7090">
            <thead data-id="element-7091">
              <tr className="border-b border-slate-200" data-id="element-7092">
                <th className="py-3 pr-4 font-bold text-slate-900" data-id="element-7093">Element</th>
                <th className="py-3 pr-4 font-bold text-slate-900" data-id="element-7094">
                  Font Size
                </th>
                <th className="py-3 pr-4 font-bold text-slate-900" data-id="element-7095">Weight</th>
                <th className="py-3 pr-4 font-bold text-slate-900" data-id="element-7096">Color</th>
                <th className="py-3 pr-4 font-bold text-slate-900" data-id="element-7097">
                  Line Height
                </th>
                <th className="py-3 font-bold text-slate-900" data-id="element-7098">Margin Bottom</th>
              </tr>
            </thead>
            <tbody className="text-slate-600 font-mono text-[13px]" data-id="element-7099">
              {[['H1', '60px', '700', '#0f172a', '1.1', '24px'], ['H2', '30px', '700', '#0f172a', '1.25', '24px'], ['H3', '20px', '700', '#0f172a', '1.4', '8px'], ['H4', '18px', '700', '#0f172a', '1.4', '8px'], ['H5', '16px', '700', '#0f172a', '1.5', '8px'], ['H6', '14px', '700', '#334155', '1.5', '8px'], ['Paragraph', '17px', '400', '#475569', '1.85', '32px'], ['Blockquote', '20px', '500', '#334155', '1.85', '0'], ['List item', '17px', '400', '#475569', '1.85', '12px'], ['Link', '17px', '500', '#2563eb', '1.85', '—'], ['Strong', 'inherit', '700', '#0f172a', 'inherit', '—'], ['Em', 'inherit', 'inherit', 'inherit', 'inherit', '—'], ['Figcaption', '14px', '400', '#94a3b8', '1.5', '0']].map(([el, size, weight, color, lh, mb]) => <tr key={el} className="border-b border-slate-100" data-id="element-7100">
                  <td className="py-3 pr-4 font-semibold text-slate-900 font-sans text-sm" data-id="element-7101">
                    {el}
                  </td>
                  <td className="py-3 pr-4" data-id="element-7102">{size}</td>
                  <td className="py-3 pr-4" data-id="element-7103">{weight}</td>
                  <td className="py-3 pr-4 flex items-center gap-2" data-id="element-7104">
                    <span className="w-3 h-3 rounded-full inline-block border border-slate-200" style={{
                  backgroundColor: color as string
                }} data-id="element-7105" />
                    {color}
                  </td>
                  <td className="py-3 pr-4" data-id="element-7106">{lh}</td>
                  <td className="py-3" data-id="element-7107">{mb}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}